import { FC, PropsWithChildren, useCallback } from 'react';

import { useApplication } from '@bonusly/application';
import { useBreakpointValue } from '@bonusly/design-system';

import { useIdentity } from '../hooks';
import { identify } from '../lib';
import { refreshToken } from '../lib/token';
import { createConfig } from '../utils/env';
import { joinPath } from '../utils/url';

interface AuthProviderProps extends PropsWithChildren {
	// Passing a JWT is intended for local development. This will bypass the need
	// to make a request with a cookie, which may not be available in a local dev.
	jwt?: string;
}

export const AuthProvider: FC<AuthProviderProps> = ({ children, jwt: providedJWT }) => {
	const { application } = useApplication();
	const { isInitialized, initialize } = useIdentity();
	const isMobile = useBreakpointValue({ base: true, sm: false });

	const loadInitialJWT = useCallback(async () => {
		if (providedJWT) {
			identify(providedJWT, { isMobile });

			return;
		}

		const jwt = await refreshToken();

		// If no JWT is available then the user is not logged in because of either an expired or missing session
		// token. In this case we need to manually redirect to the login page.
		if (!jwt) {
			const currentUrl = window.location.href;
			const redirectParam = encodeURIComponent(currentUrl);
			const loginURL = joinPath(application.recognizeOrigin, `users/sign_in_start?redirect_to=${redirectParam}`);

			window.location.assign(loginURL);

			return;
		}

		identify(jwt, { isMobile });
	}, [application, providedJWT, isMobile]);

	if (!isInitialized) {
		const config = createConfig(application);
		initialize(config);
		loadInitialJWT();
	}

	return <>{children}</>;
};
